import {
    ProductInterface,
    ProductAttribute,
} from "../interfaces/ProductInterface";
import { SimpleProduct } from "../interfaces/SimpleProduct";
import { LocalizedLabelInterface } from "../interfaces/LocalizedLabelInterface";
import { getLocalizedText } from "./getLocalizedText";
import { MetricValue } from "../interfaces/MetricValueInterface";
import { convertUnitToAbbreviation } from "./convertUnitToAbbreviation";

export const convertProduct = (
    product: ProductInterface,
    productImages: { [key: string]: string },
    format: "grid" | "list" | "related" = "grid",
): SimpleProduct => {
    let specifications: Array<{ label: string; value: string }> = [];
    let similarProductsSpecification:
        | { label: string; value: string }
        | undefined = undefined;

    // Only process specifications for grid format
    if (format === "grid") {
        specifications =
            product.productAttributes
                ?.map((attr: ProductAttribute) => {
                    const label = getLocalizedText(attr.label);
                    let value = "";

                    switch (attr.type) {
                        case "identifier":
                            value = product.identifier || "";
                            break;
                        case "text":
                            value = getLocalizedText(
                                attr.value as string | LocalizedLabelInterface,
                            );
                            break;
                        case "boolean":
                            if (
                                typeof attr.value === "object" &&
                                attr.value &&
                                Object.values(attr.value)[0] !== undefined
                            ) {
                                const boolValue = Object.values(attr.value)[0];
                                value = boolValue ? "Yes" : "No";
                            }
                            break;
                        case "metric":
                            if (
                                typeof attr.value === "object" &&
                                attr.value &&
                                Object.values(attr.value)[0]
                            ) {
                                const metricValue = Object.values(
                                    attr.value,
                                )[0] as MetricValue;
                                const amount =
                                    metricValue.amount
                                        ?.toString()
                                        .replace(/\.0000$/, "") || "";
                                const unit = metricValue.unit || "";
                                value = `${amount} ${unit}`;
                            }
                            break;
                        default:
                            if (typeof attr.value === "object" && attr.value) {
                                value = Object.values(attr.value)
                                    .map((val) =>
                                        getLocalizedText(
                                            val as LocalizedLabelInterface,
                                        ),
                                    )
                                    .join(", ");
                                break;
                            }

                            value = String(attr.value || "");
                    }

                    return { label, value };
                })
                .filter(
                    (spec: { label: string; value: string }) => spec.value,
                ) || [];
    }

    if (format === "related" && product.similarProductsSpecification) {
        const item = product.similarProductsSpecification;

        const label = getLocalizedText(item.label);
        let value = "";

        switch (true) {
            case !!item.value &&
                typeof item.value === "object" &&
                Object.values(item.value).length > 0 &&
                !!Object.values(item.value)[0] &&
                item.type !== "boolean" &&
                item.type !== "text" &&
                item.type !== "number" &&
                item.type !== "metric":
                if (Object.values(item.value).length > 1) {
                    value = Object.values(item.value)
                        .map((val) =>
                            getLocalizedText(val as LocalizedLabelInterface),
                        )
                        .join(", ");
                    break;
                }

                value = String(item.value || "");
                break;
            case !!item.value &&
                item.type === "boolean" &&
                typeof item.value === "object" &&
                !!Object.values(item.value)[0]: {
                const boolValue = Object.values(item.value)[0];
                value = boolValue ? "Yes" : "No";
                break;
            }

            case !!item.value &&
                item.type === "number" &&
                typeof item.value === "object" &&
                !!Object.values(item.value)[0]:
                value = Object.values(item.value)[0].toString();
                break;
            case !!item.value && item.type === "text":
                value = getLocalizedText(item.value as string);
                break;

            case !!item.value &&
                item.type === "metric" &&
                typeof item.value === "object" &&
                !!Object.values(item.value)[0]: {
                const metricValue = Object.values(item.value)[0] as MetricValue;
                const amount = metricValue.amount.replace(/\.0000$/, "");

                const unit = convertUnitToAbbreviation(metricValue.unit);
                value = `${amount} ${unit}`;
                break;
            }

            case item.type === "identifier":
                value = product.identifier;
                break;
        }

        if (value) {
            similarProductsSpecification = { label, value };
        }
    }

    const productImage = productImages[product.id];

    return {
        id: product.id,
        identifier: product.identifier,
        title: getLocalizedText(product.label),
        image: productImage,
        isArchived: false,
        price: undefined,
        specifications,
        similarProductsSpecification,
        originalProduct: product,
        connection: product.connection || "",
    };
};
